/**
 * The dashboard's Adventures tab: the newest adventures from the admin adventures endpoint, with
 * their publish and moderation state. The portal moderates rather than edits, so a row opens the
 * Adventures page filtered to it, and "View" opens it on the player site.
 */

import { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Box, Button, Chip, Typography } from '@mui/material'
import type { ChipProps } from '@mui/material'
import { ManageSearch, OpenInNew } from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { SearchField } from '@components/common/SearchField'
import {
  ADVENTURE_STATUS_MAP,
  MODERATION_STATUS_MAP,
  type AdminAdventureDto,
} from '@api/types/admin'
import { useRecentAdventures } from '../../hooks/useDashboardRecent'
import { errorMessage, formatDate } from '../users/userFormatting'

const PLAYER_SITE_URL = 'https://play.hadv.quest'

// Status: 0=Draft, 1=Testing, 2=Published, 3=Archived (same colours as the Adventures page).
const STATUS_COLOR: Record<number, ChipProps['color']> = {
  0: 'default',
  1: 'info',
  2: 'success',
  3: 'warning',
}

// ModerationStatus: 0=Pending, 1=Approved, 2=Rejected, 3=Flagged
const MODERATION_COLOR: Record<number, ChipProps['color']> = {
  0: 'warning',
  1: 'success',
  2: 'error',
  3: 'error',
}

/** The Adventures page reads `?search=` on load, so this lands on the one row. */
function adventureListLink(adventure: Pick<AdminAdventureDto, 'title'>): string {
  return `/adventures?search=${encodeURIComponent(adventure.title)}`
}

export function RecentAdventuresTable() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)

  const { data, isLoading, error, refetch } = useRecentAdventures(page, pageSize, search)

  const columns = useMemo<DataTableColumn<AdminAdventureDto>[]>(
    () => [
      {
        key: 'title',
        header: 'Adventure',
        primary: true,
        render: (adventure) => (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: 'anywhere' }}>
              {adventure.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {adventure.ownerDisplayName || adventure.creatorDisplayName}
            </Typography>
          </Box>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        render: (adventure) => (
          <Chip
            label={ADVENTURE_STATUS_MAP[adventure.status] ?? adventure.status}
            color={STATUS_COLOR[adventure.status] ?? 'default'}
            size="small"
          />
        ),
      },
      {
        key: 'moderation',
        header: 'Moderation',
        render: (adventure) => (
          <Chip
            label={MODERATION_STATUS_MAP[adventure.moderationStatus] ?? adventure.moderationStatus}
            color={MODERATION_COLOR[adventure.moderationStatus] ?? 'default'}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        key: 'created',
        header: 'Created',
        render: (adventure) => (
          <Typography variant="caption">{formatDate(adventure.createdDate)}</Typography>
        ),
      },
      {
        key: 'location',
        header: 'Location',
        hideBelow: 'lg',
        showInCard: false,
        render: (adventure) => (
          <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
            {adventure.location || '-'}
          </Typography>
        ),
      },
    ],
    []
  )

  return (
    <DataTable
      rows={data?.items ?? []}
      columns={columns}
      getRowKey={(adventure) => adventure.id}
      loading={isLoading}
      error={error ? errorMessage(error, 'Could not load recent adventures') : null}
      onRetry={() => refetch()}
      emptyMessage={search ? 'No adventures match that search' : 'No adventures yet'}
      page={page}
      pageSize={pageSize}
      total={data?.totalCount ?? 0}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size)
        setPage(0)
      }}
      pageSizeOptions={[5, 10, 25]}
      onRowClick={(adventure) => navigate(adventureListLink(adventure))}
      getRowActions={(adventure) => [
        {
          key: 'manage',
          label: 'Manage',
          icon: <ManageSearch />,
          hint: 'Open on the Adventures page',
          onClick: () => navigate(adventureListLink(adventure)),
        },
        {
          key: 'view',
          label: 'View',
          icon: <OpenInNew />,
          hint: 'View on the player site (new tab)',
          onClick: () =>
            window.open(`${PLAYER_SITE_URL}/adventures/${adventure.id}`, '_blank', 'noopener,noreferrer'),
        },
      ]}
      toolbar={
        <>
          <SearchField
            value={search}
            onSearch={(q) => {
              setSearch(q)
              setPage(0)
            }}
            placeholder="Search adventures"
          />
          <Button component={RouterLink} to="/adventures" size="small">
            All adventures
          </Button>
        </>
      }
    />
  )
}
